import { PropsWithChildren, ReactElement, useCallback, useMemo } from 'react'
import styles from 'components/atoms/inputs/Select/Select.styl'
import { useSelect } from 'components/atoms/inputs/Select/useSelect'
import { SelectHandler } from 'components/atoms/inputs/Select/SelectHandler/SelectHandler'
import { DropdownPreset } from 'components/atoms/inputs/Select/DropdownPreset/DropdownPreset'
import { Controller, useFormContext } from 'react-hook-form'
import { FutureSelectInput } from 'components/atoms/inputs/FutureSelectInput/FutureSelectInput'
import { Option, InputRenderer } from 'components/atoms/inputs/Select/parts/types'
import { TypeBufferHandler } from 'components/atoms/inputs/Select/TypeBufferHandler/TypeBufferHandler'

type SelectProps<Value> = {
  options: Option<Value>[]
  value: Value
  renderer?: InputRenderer
  onChange: (value: Value | null) => void
}
export const Select = <Value extends any>(props: PropsWithChildren<SelectProps<Value>>): ReactElement | null => {
  const {
    renderer,
    options,
  } = props

  const Input = useMemo(() => renderer || FutureSelectInput, [renderer])

  const selectContext = useSelect<Value>(props)
  const { isOpen, inputRef } = selectContext

  return (
    <SelectHandler context={ selectContext }>
      <TypeBufferHandler context={ selectContext } options={ options }>
        <div className={ styles.select }>
          <Input
            readOnly
            innerRef={ inputRef.set }
            className={ styles.labelInput }
            autoComplete='no'
          />
          { isOpen.visible && (
            <DropdownPreset<Value> context={ selectContext } />
          )}
        </div>
      </TypeBufferHandler>
    </SelectHandler>
  )
}

export type SelectControllerProps<Value> =
  & Pick<SelectProps<Value>, 'options' | 'renderer'>
  & {
    name: string
  }
export const SelectController = <Value extends any>(props: PropsWithChildren<SelectControllerProps<Value>>): ReactElement | null => {
  const { name, renderer, options } = props
  const { control } = useFormContext()

  const inputRenderer = useCallback(({ field: { value, onChange }}) => (
    <Select<Value>
      value={ value }
      options={ options }
      renderer={ renderer }
      onChange={ onChange }
    />
  ), [options, renderer])

  return (
    <Controller
      control={ control }
      name={ name }
      render={ inputRenderer }
    />
  )
}

